import { DialogActions, Box } from '@mui/material';
import { Button } from '@ui/buttons';
import React, { useCallback } from 'react';
import { IProjectInfo } from '@shared/models/project.model';
import { useDeleteProject } from '@client/store/project';
import DeleteIcon from '@mui/icons-material/Delete';
import { useObjectMemo } from '@client/hooks/use-object.hooks';

type EditProjectActionsProps = {
  project?: IProjectInfo;
  disabled: boolean;
  /** True while create/save is in flight so Cancel cannot unmount the form. */
  cancelDisabled?: boolean;
  onClose: () => void;
};

const EditProjectActions: React.FC<EditProjectActionsProps> = ({
  project,
  disabled,
  cancelDisabled = false,
  onClose,
}) => {
  const deleteProject = useDeleteProject();

  const handleDeleteProject = useCallback(async () => {
    if (!project) return;

    const type = project.template ? 'Project template' : 'Project';

    const deleted = await deleteProject(Number(project.id), {
      successMessage: `${type} "${project.name}" deleted`,
      errorMessage: `Failed to delete ${type} "${project.name}"`,
    });

    if (deleted) onClose();
  }, [deleteProject, project, onClose]);

  const dialogActionsStyles = useObjectMemo({
    justifyContent: project ? 'space-between' : 'flex-end',
    paddingRight: 4,
    paddingLeft: 4,
  });

  return (
    <DialogActions sx={dialogActionsStyles}>
      {project && (
        <Button
          color="neutral"
          variant="text"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteProject}
        >
          Delete
        </Button>
      )}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
        }}
      >
        <Button variant="text" onClick={onClose} disabled={cancelDisabled}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={disabled}>
          {project ? 'Save' : 'Create'}
        </Button>
      </Box>
    </DialogActions>
  );
};

export default EditProjectActions;
